/**
 * @file The component is used to display PlanItems returned by the TaskList endpoint, which needs a start and finish property.
 * @author Maarten Lensink
 */
import * as tslib_1 from "tslib";
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DomainService } from '../../domain.service';
import { UserService } from '../../user.service';
import { DialogService } from '../../core/dialog.service';
import { AlertsService } from '../../core/alerts.service';
import { OverlayService } from '../../core/overlay.service';
import { LocalStorageService } from '../../core/local-storage.service';
import { EventsService } from '../../core/event.service';
import { RelationBase } from '../../base/relation/relation.base';
import { ActionService } from '../../core/action.service';
/**
 * Displays relation entities in an inbox or outbox.
 * @export
 * @class NotificationsRelationComponent
 * @extends {RelationBase}
 */
var NotificationsRelationComponent = /** @class */ (function (_super) {
    tslib_1.__extends(NotificationsRelationComponent, _super);
    /**
     * Creates an instance of NotificationsRelationComponent.
     * @param {UserService} userService
     * @param {DomainService} domainService This service happens to have a TaskList endpoint as well.
     * @param {DialogService} dialogService
     * @param {AlertsService} alertsService
     * @param {TranslateService} translateService
     * @param {Router} router
     * @param {OverlayService} overlayService
     * @param {LocalStorageService} localStorageService
     * @param {ActivatedRoute} route
     * @param {EventsService} eventsService
     * @memberOf NotificationsRelationComponent
     */
    function NotificationsRelationComponent(userService, domainService, dialogService, alertsService, translateService, router, overlayService, localStorageService, route, eventsService, actionService) {
        var _this = _super.call(this, userService, domainService, dialogService, alertsService, translateService, router, overlayService, localStorageService, route, eventsService, actionService) || this;
        /**
         * Timeout handle of a refresh request in progress.
         * Now we are able to wait for the last query property change.
         * @private
         * @type {number}
         * @memberOf NotificationsRelationComponent
        * */
        _this.refreshInProgress = null;
        /**
         * The field is used as parameter for the TaskList endpoint.
         * @private
         * @type {Date}
         * @memberOf NotificationsRelationComponent
         */
        _this._start = null;
        /**
         * The field is used as parameter for the TaskList endpoint.
         * @private
         * @type {Date}
         * @memberOf NotificationsRelationComponent
         */
        _this._finish = null;
        return _this;
    }
    Object.defineProperty(NotificationsRelationComponent.prototype, "resourceOid", {
        /**
         * Gets the OID of the resource.
         * @readonly
         * @type {string}
         * @memberOf NotificationsRelationComponent
         */
        get: function () {
            return this.master ? this.master['Oid'] : '';
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(NotificationsRelationComponent.prototype, "start", {
        /**
         * Gets or sets the start parameter of the TaskList endpoint. Refreshes after set.
         * @type {Date}
         * @memberOf NotificationsRelationComponent
         */
        get: function () {
            return this._start;
        },
        set: function (value) {
            if (value && (!this._start || (this._start.getTime() !== value.getTime()))) {
                this._start = value;
                this.refresh();
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(NotificationsRelationComponent.prototype, "finish", {
        /**
         * Gets or sets the finish parameter of the TaskList endpoint. Refreshes after set.
         * @type {Date}
         * @memberOf NotificationsRelationComponent
         */
        get: function () {
            return this._finish;
        },
        set: function (value) {
            if (value && (!this._finish || (this._finish.getTime() !== value.getTime()))) {
                this._finish = value;
                this.refresh();
            }
        },
        enumerable: true,
        configurable: true
    });
    /**
     * A property has changed. A refresh is needed only when the last query property has changed.
     * @private
     * @memberOf NotificationsRelationComponent
     */
    NotificationsRelationComponent.prototype.processChange = function () {
        var _this = this;
        // When we need loading indicators.
        this.isDataLoaded = false;
        this.isRefreshing = true;
        if (this.refreshInProgress) {
            // This was not the last query property changed.
            // We cancel the refresh in progress.
            window.clearTimeout(this.refreshInProgress);
        }
        // We will start a refresh soon.
        this.refreshInProgress = window.setTimeout(function () {
            _this.refreshInProgress = null;
            _super.prototype.refresh.call(_this);
        }, 500);
    };
    /**
     * Overrides to make sure refresh doesn't happen when start or finish are not set yet.
     * @returns {Observable<QueryResult>}
     * @memberOf NotificationsRelationComponent
     */
    NotificationsRelationComponent.prototype.refresh = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            return tslib_1.__generator(this, function (_a) {
                if (!this.start || !this.finish) {
                    return [2 /*return*/, null];
                }
                this.processChange();
                return [2 /*return*/, null];
            });
        });
    };
    return NotificationsRelationComponent;
}(RelationBase));
export { NotificationsRelationComponent };
